import type { ValidIcon } from '@/components/ui/Icon';
import anchorIcon from './anchoring-phenomenon.svg';
import assessmentsIcon from './assessments@2x.svg';
import challengeIcon from './challenge-icon.svg';
import checkIcon from './check.svg';
import discussIcon from './discuss.svg';
import explainAndProcessIcon from './explain-and-processing.svg';
import floridaIcon from './florida.svg';
import gamesIcon from './games.svg';
import geographyChallengeIcon from './geography-challenge.svg';
import hoiIcon from './HOI.svg';
import learningTargetIcon from './learning-target.svg';
import localConnectionsIcon from './local-connections.svg';
import observeIcon from './observe.svg';
import overviewIcon from './overview.svg';
import readingIcon from './reading.svg';
import readingCheckIcon from './reading-check.svg';
import settingsIcon from './settings.svg';
import timelineChallengeIcon from './timeline-challenge.svg';
import videoActivityIcon from './video-activity.svg';
import vocabIcon from './vocab.svg';
import ssWritingIcon from './writing-in-ss.svg';

/* eslint-disable sort-keys -- render order in IconSelect's grid; keep common types first */
const icons = {
  readingIcon,
  vocabIcon,
  hoiIcon,
  observeIcon,
  explainAndProcessIcon,
  videoActivityIcon,
  gamesIcon,
  assessmentsIcon,
  settingsIcon,
  checkIcon,
  anchorIcon,
  readingCheckIcon,
  discussIcon,
  localConnectionsIcon,
  overviewIcon,
  timelineChallengeIcon,
  geographyChallengeIcon,
  challengeIcon,
  ssWritingIcon,
  learningTargetIcon,
  floridaIcon,
};
/* eslint-enable sort-keys */

// The vocabulary slide_groups.icon stores, in IconSelect's grid order. `legacyKey` is the value the
// column held before PLAT-17854's rename and the key into the SVG map above; keeping both means the
// site renders on either side of that data migration. Mirrors ICON_RENAMES in
// db/data/20260903120000_rename_slide_group_icons_to_ui_icon_names.rb.
export const SLIDE_GROUP_ICONS: ReadonlyArray<{ icon: ValidIcon; labelKey: string; legacyKey: keyof typeof icons; }> = [
  { icon: 'Reading', labelKey: 'reading', legacyKey: 'readingIcon' },
  { icon: 'Vocabulary', labelKey: 'vocabulary', legacyKey: 'vocabIcon' },
  { icon: 'HandsOn', labelKey: 'hands_on_investigation', legacyKey: 'hoiIcon' },
  { icon: 'Observe', labelKey: 'observe', legacyKey: 'observeIcon' },
  { icon: 'PencilBubble', labelKey: 'explain_and_process', legacyKey: 'explainAndProcessIcon' },
  { icon: 'VideoActivity', labelKey: 'video_activity', legacyKey: 'videoActivityIcon' },
  { icon: 'Games', labelKey: 'games', legacyKey: 'gamesIcon' },
  { icon: 'Assessments', labelKey: 'assessments', legacyKey: 'assessmentsIcon' },
  { icon: 'Settings', labelKey: 'settings', legacyKey: 'settingsIcon' },
  { icon: 'CheckmarkCircle', labelKey: 'check', legacyKey: 'checkIcon' },
  { icon: 'Anchor', labelKey: 'anchoring_phenomenon', legacyKey: 'anchorIcon' },
  { icon: 'ReadingCheck', labelKey: 'reading_check', legacyKey: 'readingCheckIcon' },
  { icon: 'Discuss', labelKey: 'discuss', legacyKey: 'discussIcon' },
  { icon: 'LocalConnections', labelKey: 'local_connections', legacyKey: 'localConnectionsIcon' },
  { icon: 'Overview', labelKey: 'overview', legacyKey: 'overviewIcon' },
  { icon: 'Timeline', labelKey: 'timeline_challenge', legacyKey: 'timelineChallengeIcon' },
  { icon: 'GeoCompass', labelKey: 'geography_challenge', legacyKey: 'geographyChallengeIcon' },
  { icon: 'Inquiry', labelKey: 'challenge', legacyKey: 'challengeIcon' },
  { icon: 'Writing', labelKey: 'writing_in_social_studies', legacyKey: 'ssWritingIcon' },
  { icon: 'Target', labelKey: 'learning_target', legacyKey: 'learningTargetIcon' },
  { icon: 'Florida', labelKey: 'florida', legacyKey: 'floridaIcon' },
];

// Accepts either vocabulary — a pre-rename camelCase value or a ui/Icon name.
export const findSlideGroupIcon = (value?: string | null) => (
  SLIDE_GROUP_ICONS.find(({ icon, legacyKey }) => value === icon || value === legacyKey)
);

export const toValidIcon = (value?: string | null): ValidIcon | undefined => (
  findSlideGroupIcon(value)?.icon
);

export const toIconSrc = (value?: string | null): string | undefined => {
  const entry = findSlideGroupIcon(value);

  return entry && icons[entry.legacyKey];
};

export default icons;
